/**
 * Hook: Nostr relay status — checks connectivity of the app's relay list.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '@/hooks/useAppContext';

export interface RelayStatus {
  url: string;
  status: 'online' | 'connecting' | 'offline';
}

export function useRelayStatus() {
  const { config } = useAppContext();
  const [relays, setRelays] = useState<RelayStatus[]>([]);

  const checkRelays = useCallback(async () => {
    const relayList = config.relayMetadata.relays;

    const statuses: RelayStatus[] = relayList.map((r) => ({
      url: r.url,
      status: 'connecting' as const,
    }));
    setRelays(statuses);

    const results = await Promise.allSettled(
      relayList.map(async (r) => {
        try {
          const ws = new WebSocket(r.url);
          const timeout = setTimeout(() => {
            ws.close();
          }, 5000);
          await new Promise<void>((resolve, reject) => {
            ws.onopen = () => { clearTimeout(timeout); resolve(); };
            ws.onerror = () => { clearTimeout(timeout); reject(); };
          });
          ws.close();
          return { url: r.url, status: 'online' as const };
        } catch {
          return { url: r.url, status: 'offline' as const };
        }
      }),
    );

    setRelays(
      results.map((r) =>
        r.status === 'fulfilled'
          ? r.value
          : { url: '', status: 'offline' as const },
      ),
    );
  }, [config.relayMetadata.relays]);

  useEffect(() => {
    checkRelays();
    const interval = setInterval(checkRelays, 60000);
    return () => clearInterval(interval);
  }, [checkRelays]);

  const overallStatus: 'online' | 'connecting' | 'offline' =
    relays.length === 0 ? 'offline' :
    relays.some((r) => r.status === 'online') ? 'online' :
    relays.some((r) => r.status === 'connecting') ? 'connecting' :
    'offline';

  return { relays, overallStatus, checkRelays };
}
